import { useState, useEffect } from 'react';
import { Search, Shield, AlertTriangle, CheckCircle, X, Info } from 'lucide-react';
import { pqpProtection } from '@/lib/crypto/pqp-protection';
import { blockchainAPI } from '@/lib/api/blockchain-api';
import { bitcoinOutputAnalyzer } from '@/lib/crypto/bitcoin-output-analyzer';

interface AddressAnalysis {
  addressType: 'P2PK' | 'P2PKH' | 'P2SH' | 'P2WPKH' | 'P2WSH' | 'P2TR' | 'Unknown';
  network: 'mainnet' | 'testnet' | 'regtest';
  isValid: boolean;
  isSafe: boolean;
  hasSpent: boolean;
  reason: string;
  details?: string;
  balance?: number; // in satoshis
  txCount?: number;
  sentTxCount?: number;
  receivedTxCount?: number;
  hasTransactions?: boolean;
  address?: string; // The actual address being analyzed
}

export function AddressStatusBar() {
  const [isOpen, setIsOpen] = useState(true); // Start open as call-to-action
  const [address, setAddress] = useState('');
  const [analysis, setAnalysis] = useState<AddressAnalysis | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  // Enhanced address analysis combining hardcoded special cases with output analyzer
  const analyzeAddress = async (addr: string): Promise<AddressAnalysis> => {
    // Basic validation
    if (!addr || addr.length < 26) {
      return { addressType: 'Unknown', network: 'mainnet', isValid: false, isSafe: false, hasSpent: false, reason: 'Invalid address format' };
    }
    
    // Special cases: Known P2PK addresses from very early Bitcoin blocks
    // These are kept for fast identification of historically significant addresses
    const knownSpecialCases: Record<string, AddressAnalysis> = {
      '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa': {
        addressType: 'P2PK',
        network: 'mainnet',
        isValid: true,
        isSafe: false,
        hasSpent: false, // Genesis block never spent (unspendable)
        reason: 'Genesis block P2PK - Satoshi\'s first coin',
        details: 'Genesis block coinbase output. Contains the first Bitcoin ever created. P2PK format with exposed public key.',
        address: addr
      },
      '12cbQLTFMXRnSzktFkuoG3eHoMeFtpTu3S': {
        addressType: 'P2PK',
        network: 'mainnet', 
        isValid: true,
        isSafe: false,
        hasSpent: false, // Block 1 coinbase never spent
        reason: 'Block 1 coinbase P2PK - First spendable Bitcoin',
        details: 'Block 1 coinbase output. First technically spendable Bitcoin. P2PK format with exposed public key.',
        address: addr
      }
    };
    
    // Check for special cases first
    if (knownSpecialCases[addr]) {
      return knownSpecialCases[addr];
    }
    
    // Determine network based on address prefix
    let network: 'mainnet' | 'testnet' | 'regtest' = 'mainnet';
    
    // Testnet detection
    if (addr.startsWith('m') || addr.startsWith('n') || // P2PKH testnet
        addr.startsWith('2') || // P2SH testnet
        addr.startsWith('tb1') || // Bech32 testnet
        addr.startsWith('tpub') || addr.startsWith('tprv')) { // Extended keys testnet
      network = 'testnet';
    }
    // Regtest detection (bcrt1 for bech32 regtest)
    else if (addr.startsWith('bcrt1')) {
      network = 'regtest';
    }

    // Try to use the bitcoin output analyzer for comprehensive analysis
    try {
      // For now, we'll try to analyze using blockchain data if this looks like a standard address
      // The analyzer can detect P2PK vs P2PKH by analyzing actual transaction outputs
      
      // First, do a quick local format validation to determine likely type
      const quickAnalysis = await analyzeAddressFormat(addr, network);
      
      // If it's a format we can analyze with the blockchain, try that
      if (quickAnalysis.isValid && (quickAnalysis.addressType === 'P2PKH' || quickAnalysis.addressType === 'P2SH')) {
        // For P2PKH addresses that might actually be P2PK, we'll let the blockchain API
        // with the integrated output analyzer make the final determination
        return quickAnalysis; // Return local analysis, blockchain API will enhance it
      }
      
      return quickAnalysis;
      
    } catch (error) {
      console.error('Address analysis error:', error);
      // Fall back to local analysis
      return await analyzeAddressFormat(addr, network);
    }
  };

  // Local format-based analysis (fallback and quick analysis)
  const analyzeAddressFormat = async (addr: string, network: 'mainnet' | 'testnet' | 'regtest'): Promise<AddressAnalysis> => {
    // P2PKH (Legacy addresses starting with 1)
    if (addr.startsWith('1') && addr.length >= 26 && addr.length <= 35) {
      return {
        addressType: 'P2PKH',
        network,
        isValid: true,
        isSafe: true, // Safe until spent
        hasSpent: false, // Would need blockchain lookup
        reason: 'Legacy address - Safe if never spent',
        details: 'Public key hidden until first spend. Check blockchain for spend status.'
      };
    }

    // P2SH (Multisig/SegWit nested, starting with 3)
    if (addr.startsWith('3') && addr.length >= 26 && addr.length <= 35) {
      return {
        addressType: 'P2SH',
        network,
        isValid: true,
        isSafe: true,
        hasSpent: false,
        reason: 'P2SH address - Script hidden until spent',
        details: 'Commonly used for multisig or nested SegWit. Safe until redemption.'
      };
    }

    // Bech32 addresses (bc1)
    if (addr.startsWith('bc1') || addr.startsWith('tb1')) {
      // P2TR (Taproot) or P2WSH - bc1p
      if (addr.startsWith('bc1p') || addr.startsWith('tb1p')) {
        if (addr.length === 62) {
          // P2TR (Taproot) - exactly 62 characters
          return {
            addressType: 'P2TR',
            network,
            isValid: true,
            isSafe: false, // Always vulnerable - Taproot exposes public keys
            hasSpent: false,
            reason: 'Taproot address - Quantum vulnerable',
            details: 'Taproot exposes x-only public keys. Not quantum safe.'
          };
        } else if (addr.length > 62) {
          // P2WSH with v1+ witness version - longer than 62 chars
          return {
            addressType: 'P2WSH',
            network,
            isValid: true,
            isSafe: true, // Safe until spent
            hasSpent: false,
            reason: 'P2WSH address - Safe if never spent',
            details: 'Pay-to-Witness-Script-Hash. Script hidden until spent, making it quantum safe until redemption.'
          };
        } else {
          // Invalid length
          return {
            addressType: 'Unknown',
            network,
            isValid: false,
            isSafe: false,
            hasSpent: false,
            reason: `Invalid bech32 address length (${addr.length} chars)`,
            details: 'P2TR should be 62 chars, P2WSH should be >62 chars.'
          };
        }
      }
      
      // P2WPKH/P2WSH (Native SegWit) - bc1q
      if (addr.startsWith('bc1q') || addr.startsWith('tb1q')) {
        // According to BIP-173 specification:
        // P2WPKH addresses are EXACTLY 42 characters
        // P2WSH addresses are EXACTLY 62 characters
        const isP2WPKH = addr.length === 42;
        const isP2WSH = addr.length === 62;
        
        // Special case: Some addresses might have non-standard lengths
        // In this case, we'll still try to validate them but flag them
        if (!isP2WPKH && !isP2WSH) {
          // For non-standard lengths, make a best guess based on length
          // but still mark as valid to avoid blocking legitimate addresses
          let addressType: 'P2WPKH' | 'P2WSH' = 'P2WPKH';
          let details = `Non-standard SegWit address length (${addr.length} chars). `;
          
          if (addr.length < 50) {
            addressType = 'P2WPKH';
            details += 'Likely P2WPKH (standard is 42 chars).';
          } else {
            addressType = 'P2WSH';
            details += 'Likely P2WSH (standard is 62 chars).';
          }
          
          return {
            addressType,
            network,
            isValid: true, // Still valid, just non-standard length
            isSafe: true, // Safe until spent
            hasSpent: false,
            reason: `${addressType} (non-standard length) - Safe if never spent`,
            details
          };
        }
        
        return {
          addressType: isP2WPKH ? 'P2WPKH' : 'P2WSH',
          network,
          isValid: true,
          isSafe: true, // Safe until spent
          hasSpent: false,
          reason: `${isP2WPKH ? 'Native SegWit' : 'SegWit Script'} - Safe if never spent`,
          details: 'SegWit addresses provide quantum protection until spent. Only a hash is exposed in the unspent output.'
        };
      }
    }

    return { addressType: 'Unknown', network, isValid: false, isSafe: false, hasSpent: false, reason: 'Unrecognized address format' };
  };

  const checkAddress = async () => {
    if (!address.trim()) return;
    
    setIsChecking(true);
    
    // First do local analysis (now async)
    const localAnalysis = await analyzeAddress(address);
    setAnalysis(localAnalysis);
    
    // Then check with API if valid
    if (localAnalysis.isValid) {
      try {
        // Get real blockchain data
        const blockchainInfo = await blockchainAPI.getAddressInfo(address);
        
        // Update address type if blockchain confirms it's P2PK
        let finalAddressType = localAnalysis.addressType;
        if (blockchainInfo.isP2PK && localAnalysis.addressType === 'P2PKH') {
          finalAddressType = 'P2PK';
        }
        
        // Get protection analysis
        const protection = await pqpProtection.needsQuantumProtection(address);
        
        // Update analysis with real data
        // For address types that are safe until spent, update safety based on spending status
        const isSafeBasedOnType = finalAddressType !== 'P2PK' && finalAddressType !== 'P2TR';
        const isSafe = isSafeBasedOnType && !blockchainInfo.hasSpent;
        
        // Debug output for spending detection
        if (blockchainInfo.txCount && blockchainInfo.txCount > 1000) {
          console.log(`Address analysis for ${address}:`, {
            addressType: finalAddressType,
            hasSpent: blockchainInfo.hasSpent,
            isSafeBasedOnType,
            isSafe,
            txCount: blockchainInfo.txCount
          });
        }
        
        setAnalysis(prev => ({
          ...prev!,
          addressType: finalAddressType,
          hasSpent: blockchainInfo.hasSpent,
          isSafe: isSafe,
          reason: finalAddressType === 'P2PK' 
            ? 'P2PK output - public key exposed in blockchain'
            : protection.reason || prev!.reason,
          balance: blockchainInfo.balance,
          txCount: blockchainInfo.txCount,
          sentTxCount: blockchainInfo.sentTxCount,
          receivedTxCount: blockchainInfo.receivedTxCount,
          hasTransactions: blockchainInfo.hasTransactions,
          address: address // Pass the address for tooltip
        }));
      } catch (error) {
        console.error('Error checking address:', error);
        // Keep local analysis if API fails
      }
    }
    
    setIsChecking(false);
  };

  // Auto-check on valid address format
  useEffect(() => {
    if (address.length >= 26) {
      const timer = setTimeout(() => {
        checkAddress();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setAnalysis(null);
    }
  }, [address]); // eslint-disable-line react-hooks/exhaustive-deps

  const getStatusIcon = () => {
    if (isChecking) {
      return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500" />;
    }
    if (!analysis) {
      return <Shield className="w-5 h-5 text-gray-400" />;
    }
    if (analysis.isSafe) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
    return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
  };

  const getStatusColor = () => {
    if (!analysis) return 'text-gray-500';
    if (analysis.isSafe) return 'text-green-500';
    return 'text-yellow-500';
  };

  // Dark variants added 2026-08: this bar shipped with none, so it rendered as
  // a light-grey slab across the bottom of every dark-theme page on every
  // brand. Harmless-looking against the old gray-900 app ground; unmissable
  // against the marketing ground.
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 py-3 px-4 transition-colors duration-200 border-t border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-center space-x-2">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">Test Your Address Quantum Safety</span>
          </div>
        </button>
      ) : (
        <div className="bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-start space-x-4">
              <div className="flex-1">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex items-center space-x-3">
                  <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Test your address quantum safety - Enter any Bitcoin address"
                    className="flex-1 outline-none text-gray-900 placeholder-gray-400 text-sm"
                    autoFocus
                  />
                  {address && (
                    <button
                      onClick={() => setAddress('')}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {/* Status info - single line to the right */}
                  {analysis && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600 border-l pl-3 ml-2">
                      {analysis.isValid ? (
                        <>
                          <span 
                            className={`font-medium ${
                              analysis.isSafe ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {analysis.addressType}
                          </span>
                          <span className="text-gray-400">•</span>
                          <span className="capitalize">{analysis.network}</span>
                          <span className="text-gray-400">•</span>
                          <span>
                            {analysis.balance !== undefined && analysis.balance > 0 
                              ? `${(analysis.balance / 100000000).toFixed(8)} BTC`
                              : 'No funds'}
                            {analysis.sentTxCount !== undefined && analysis.receivedTxCount !== undefined
                              ? (analysis.sentTxCount > 0 || analysis.receivedTxCount > 0)
                                ? `, ${analysis.sentTxCount} tx ${analysis.receivedTxCount} rx`
                                : ', No Transaction History'
                              : ', No Transaction History'}
                          </span>
                          <span className="text-gray-400">•</span>
                          <span className={`font-medium ${
                            analysis.isSafe ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {analysis.isSafe ? 
                              (analysis.addressType !== 'P2PK' && analysis.addressType !== 'P2TR' ? 'pqsafe (until spent)' : 'pqsafe') : 
                              'pqrisk'}
                          </span>
                        </>
                      ) : (
                        <span className="text-red-600 font-medium">{analysis.reason}</span>
                      )}
                      {/* Info tooltip */}
                      <div className="relative group">
                        <Info className="w-4 h-4 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-50">
                          <div className="bg-gray-900 text-white text-xs rounded-lg py-3 px-4 shadow-lg" style={{ minWidth: '320px' }}>
                            <div className="space-y-1">
                              <p><strong>{analysis.addressType}</strong>: {
                                analysis.addressType === 'P2PKH' ? 'Pay to Public Key Hash (Legacy)' :
                                analysis.addressType === 'P2SH' ? 'Pay to Script Hash (Multisig/SegWit nested)' :
                                analysis.addressType === 'P2WPKH' ? 'Pay to Witness Public Key Hash (Native SegWit)' :
                                analysis.addressType === 'P2WSH' ? 'Pay to Witness Script Hash' :
                                analysis.addressType === 'P2TR' ? 'Pay to Taproot' :
                                analysis.addressType === 'P2PK' ? 'Pay to Public Key (Very early Bitcoin)' :
                                'Unknown address type'
                              }</p>
                              {(analysis.address === '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa' || analysis.address === '12cbQLTFMXRnSzktFkuoG3eHoMeFtpTu3S') && (
                                <p className="text-xs italic">
                                  Note: {analysis.address === '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa' ? 'Genesis block (Block 0)' : 'Block 1 coinbase'} - actual output is P2PK, not P2PKH
                                </p>
                              )}
                              <p><strong>Network</strong>: {analysis.network}</p>
                              <p><strong>tx</strong>: Transactions sent (transmitted from this address)</p>
                              <p><strong>rx</strong>: Transactions received (received to this address)</p>
                              <p><strong>Quantum Safety</strong>: {
                                analysis.isSafe 
                                  ? analysis.addressType === 'P2PK' || analysis.addressType === 'P2TR'
                                    ? 'Address marked safe (special case or unspendable)'
                                    : 'Safe until spent - only a hash is exposed in the unspent output. Public key remains hidden, protecting against quantum attacks.'
                                  : analysis.addressType === 'P2TR' || analysis.addressType === 'P2PK'
                                    ? 'Not quantum-safe - public key is exposed in the output itself, vulnerable to Shor\'s algorithm'
                                    : 'No longer quantum-safe - public key has been revealed by spending transactions'
                              }</p>
                              <p className="text-xs mt-1 italic">
                                {['P2PKH', 'P2SH', 'P2WPKH', 'P2WSH'].includes(analysis.addressType) && 
                                 'All hash-based addresses (P2PKH, P2SH, P2WPKH, P2WSH) are quantum-safe until spent.'}
                              </p>
                            </div>
                            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={() => {
                  setIsOpen(false);
                  setAddress('');
                  setAnalysis(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
